import React from "react";
import { withStyles, createStyles, WithStyles, Theme } from "@material-ui/core/styles";

import Grid from "../lib/components/grid";
import { SailingScheduleColumns } from "./sailingloadouttablesetup";
import FilterButtons, { FilterState, VisibleColumns } from "./components/FilterButtons";

type SailingScheduleGridProps = {
    data?: any[];
    loading?: boolean;
    clearFilters?: string;
    handleRefresh?: () => void;
    rowRenderer?: (props: any) => React.ReactElement;
    totalRowColumns?: string[];
    menuItems?: any;
    vesseloptions?: any[];
    portoptions?: any[];
    shippinglines?: any[];
    GridColumns?: (data: any[], filters: any, arrangement: any, columnsWidth: any) => any[];
    headers?: Record<string, string>;
    onFilterChange?: (filters: FilterState) => void;
    buttonFilters?: FilterState;
    onProcess?: (visibleColumns: VisibleColumns) => void;
    isProcessed?: boolean;
    onReset?: () => void;
    gridHeight?: number;
} & WithStyles<typeof styles>;

const styles = (theme: Theme) =>
    createStyles({
        root: {
            height: "100%",
            width: "100%",
            position: "relative",
        },
        filterButtonsContainer: {
            position: "absolute",
            top: "8px",
            left: "8px",
            zIndex: 1000,
            padding: "4px",
        },
    });

class SailingScheduleGridUnstyled extends React.Component<SailingScheduleGridProps, {
    appliedFilters: FilterState | null;
    pendingFilters: FilterState | null;
}> {
    private defaultFilters: FilterState = {
        market: [],
        portOfLoading: [],
        portOfDischarge: [],
    };

    state = {
        appliedFilters: null as FilterState | null,
        pendingFilters: null as FilterState | null, //filters reselected goes into pending state until processed button is hit again
    };

    componentDidUpdate(prevProps: SailingScheduleGridProps) {
        if (this.props.isProcessed && !prevProps.isProcessed) {
            this.setState({
                appliedFilters: this.props.buttonFilters || this.defaultFilters,
                pendingFilters: null
            });
        }

        if (this.props.isProcessed && prevProps.isProcessed) {
            const filtersChanged = JSON.stringify(this.props.buttonFilters) !==
                JSON.stringify(prevProps.buttonFilters);

            if (filtersChanged) {
                this.setState({
                    pendingFilters: this.props.buttonFilters || this.defaultFilters
                });
            }
        }

        if (!this.props.isProcessed && prevProps.isProcessed) {
            this.setState({
                appliedFilters: null,
                pendingFilters: null
            });
        }
    }

    handleProcess = (visibleColumns: VisibleColumns) => {
        this.setState({
            appliedFilters: this.state.pendingFilters || this.props.buttonFilters || this.defaultFilters,
            pendingFilters: null
        });

        if (this.props.onProcess) {
            this.props.onProcess(visibleColumns);
        }
    };

    getFilteredData = () => {
        const { data = [], headers = {} } = this.props;
        const { appliedFilters } = this.state;

        if (!appliedFilters) { //only apply filter when process button was clicked, not immediately when reselected
            return data;
        }

        const hasActiveFilters =
            appliedFilters.market.length > 0 ||
            appliedFilters.portOfLoading.length > 0 ||
            appliedFilters.portOfDischarge.length > 0;

        if (!hasActiveFilters) {
            return data;
        }

        return data.filter(row => {
            if (appliedFilters.market.length > 0) {
                if (!row.market || !appliedFilters.market.includes(row.market)) {
                    return false;
                }
            }

            if (appliedFilters.portOfLoading.length > 0) {
                if (!row.port_of_loading || !appliedFilters.portOfLoading.includes(row.port_of_loading)) {
                    return false;
                }
            }

            if (appliedFilters.portOfDischarge.length > 0) {
                const selectedPortKeys: string[] = [];
                Object.entries(headers).forEach(([key, portName]) => {
                    if (appliedFilters.portOfDischarge.includes(portName)) {
                        selectedPortKeys.push(key);
                    }
                });

                const hasDataInSelectedPort = selectedPortKeys.some(key => {
                    const value = row[key];
                    return value && value !== '' && value !== '-';
                });

                if (!hasDataInSelectedPort) {
                    return false;
                }
            }

            return true;
        });
    };

    render() {
        const {
            classes,
            loading = false,
            clearFilters,
            handleRefresh,
            rowRenderer,
            totalRowColumns,
            menuItems,
            vesseloptions = [],
            portoptions = [],
            shippinglines = [],
            GridColumns,
            headers = {},
            onFilterChange,
            buttonFilters,
            onProcess,
            isProcessed = false,
            onReset,
            gridHeight,
        } = this.props;

        const filters = buttonFilters || this.defaultFilters;
        const handleFilterChange = onFilterChange || (() => { });
        const handleReset = onReset || (() => { });

        const displayData = this.getFilteredData();
        const hasPendingFilters = this.state.pendingFilters !== null;

        return (
            <div className={classes.root}>
                <div className={classes.filterButtonsContainer}>
                    <FilterButtons
                        data={this.props.data || []}
                        headers={headers}
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        onProcess={this.handleProcess}
                        isProcessed={isProcessed && !hasPendingFilters}
                        onReset={handleReset}
                    />
                </div>
                <Grid
                    fill
                    forceHeight={gridHeight}
                    loading={loading}
                    data={displayData}
                    GridColumns={GridColumns || ((data, filters, columnArrangement, columnsWidth) =>
                        SailingScheduleColumns(
                            data,
                            filters,
                        )
                    )}
                    rowRenderer={rowRenderer}
                    handleRefresh={handleRefresh}
                    clearFilters={clearFilters}
                    totalRowColumns={totalRowColumns}
                    menuItems={menuItems}
                />
            </div>
        );
    }
}

export default withStyles(styles)(SailingScheduleGridUnstyled);